import type { TPlayerDiagnosticCommand, TPlayerDiagnosticCommandResult } from '@lucimark/shared/models/player-remote-diagnostics'
import { playerDiagnosticCommandResultSchema } from '@lucimark/shared/models/player-remote-diagnostics'
import { playerDiagnosticsSnapshotSchema } from '@lucimark/shared/models/player-diagnostics'
import type { APIClientService } from '@/services/APIClientService'
import {
  buildPlayerDiagnosticsSnapshot,
  clearPlayerDiagnosticEvents,
  initPlayerDiagnosticsWorker,
  runPlayerDiagnosticsSafely
} from '@/helpers/playerDiagnostics/playerDiagnosticService'
import {
  armPlayerDiagnosticSession,
  getPlayerDiagnosticSessionActiveUntilMs
} from '@/helpers/playerDiagnostics/playerDiagnosticsKillSwitch'

function isCommandExpired(command: TPlayerDiagnosticCommand, nowMs: number): boolean {
  return nowMs > command.issuedAt + command.ttlSeconds * 1000
}

/**
 * Ensure the POST body will pass API `.strict()` validation. A 400 leaves the operator
 * poll stuck on pending until TTL (looks like a mysterious timeout).
 */
export function sanitizePlayerDiagnosticCommandResult(
  result: TPlayerDiagnosticCommandResult
): TPlayerDiagnosticCommandResult {
  if (!result.ok || result.snapshot == null) {
    return result
  }
  const snap = playerDiagnosticsSnapshotSchema.safeParse(result.snapshot)
  if (!snap.success) {
    console.warn(JSON.stringify({
      tag: 'player_diagnostic_snapshot_invalid',
      commandId: result.commandId,
      issues: snap.error.issues.slice(0, 8).map(i => ({ path: i.path.join('.'), code: i.code, message: i.message }))
    }))
    return {
      commandId: result.commandId,
      ok: false,
      error: 'snapshot_invalid'
    }
  }
  const sanitized: TPlayerDiagnosticCommandResult = {
    commandId: result.commandId,
    ok: true,
    snapshot: snap.data
  }
  if (result.sessionActiveUntil != null) {
    sanitized.sessionActiveUntil = result.sessionActiveUntil
  }
  const body = playerDiagnosticCommandResultSchema.safeParse(sanitized)
  if (!body.success) {
    return {
      commandId: result.commandId,
      ok: false,
      error: 'snapshot_invalid'
    }
  }
  return body.data
}

async function postCommandResult(
  apiClient: APIClientService,
  result: TPlayerDiagnosticCommandResult
): Promise<void> {
  const payload = sanitizePlayerDiagnosticCommandResult(result)
  try {
    await apiClient.postPlayerDiagnosticCommandResult(payload)
  }
  catch (err) {
    console.warn(JSON.stringify({
      tag: 'player_diagnostic_command_result_failed',
      commandId: payload.commandId,
      ok: payload.ok,
      message: err instanceof Error ? err.message : String(err)
    }))
  }
}

/** Pure command executor (testable; does not POST results). */
export function executePlayerDiagnosticCommand(command: TPlayerDiagnosticCommand): TPlayerDiagnosticCommandResult {
  const nowMs = Date.now()
  if (isCommandExpired(command, nowMs)) {
    return {
      commandId: command.commandId,
      ok: false,
      error: 'command_expired'
    }
  }

  switch (command.type) {
    case 'getDiagnosticsSnapshot': {
      const snapshot = buildPlayerDiagnosticsSnapshot(nowMs)
      if (snapshot == null) {
        return {
          commandId: command.commandId,
          ok: false,
          error: 'diagnostics_disabled'
        }
      }
      return {
        commandId: command.commandId,
        ok: true,
        snapshot
      }
    }
    case 'clearDiagnosticEvents':
      clearPlayerDiagnosticEvents()
      return {
        commandId: command.commandId,
        ok: true
      }
    case 'enableDiagnosticSession': {
      const untilMs = nowMs + command.sessionTtlSeconds * 1000
      armPlayerDiagnosticSession(untilMs)
      initPlayerDiagnosticsWorker()
      return {
        commandId: command.commandId,
        ok: true,
        sessionActiveUntil: getPlayerDiagnosticSessionActiveUntilMs()
      }
    }
    default: {
      return {
        commandId: (command as { commandId: string }).commandId,
        ok: false,
        error: 'unknown_command'
      }
    }
  }
}

/**
 * Fire-and-forget handler for operator diagnostic WS commands. Must not block playback.
 */
export function handlePlayerDiagnosticCommand(
  apiClient: APIClientService,
  command: TPlayerDiagnosticCommand
): void {
  runPlayerDiagnosticsSafely(() => {
    const result = executePlayerDiagnosticCommand(command)
    void postCommandResult(apiClient, result)
  })
}
